import React, { useEffect, useState } from 'react';
import { Copy, KeyRound, RefreshCw } from 'lucide-react';
import { authservice } from '../../../../components/api/authservice';
import { MCP_JWT_DEFAULT_TTL_SECONDS, MCP_JWT_EXPIRY_OPTIONS, SINGLETON_ENDPOINT_ID, TENANT_ID, clientTypeLabel } from '../governedMcpConstants';
import { governedMcpApi } from '../governedMcpApi';
import { logGovernedMcp } from '../governedMcpLogger';
import { createId, useGovernedMcp } from '../governedMcpStore';
import type { GatewayApiKey, McpClient } from '../governedMcpTypes';
import { GatewayDrawer, fieldClass, ghostBtn, labelClass, primaryBtn } from '../components/GatewayUi';

type AuthUser = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  tenantId?: string;
  roleID?: string;
  roleName?: string;
  isadmin?: boolean;
  isActive?: boolean;
};

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function normalizeAuthUser(row: Record<string, unknown>): AuthUser | null {
  const id = asString(row.id || row.userId || row.userID || row.user_id);
  if (!id) return null;
  return {
    id,
    email: asString(row.email),
    firstName: asString(row.firstName || row.first_name) || undefined,
    lastName: asString(row.lastName || row.last_name) || undefined,
    tenantId: asString(row.tenantId || row.tenant_id || row.TenantId) || TENANT_ID,
    roleID: asString(row.roleID || row.roleId || row.role_id || row.role),
    roleName: asString(row.roleName || row.role_name) || undefined,
    isadmin: Boolean(row.isadmin ?? row.isAdmin ?? row.is_admin),
    isActive: row.isActive === undefined && row.is_active === undefined ? undefined : Boolean(row.isActive ?? row.is_active),
  };
}

function userDisplayName(user: AuthUser): string {
  const parts = [user.firstName?.trim(), user.lastName?.trim()].filter(Boolean);
  return parts.length ? parts.join(' ') : user.email || user.id;
}

function userDetails(user: AuthUser): string {
  const bits = [
    user.roleName ? `Role: ${user.roleName}` : '',
    user.isadmin ? 'Admin' : '',
    user.isActive === false ? 'Inactive' : user.isActive ? 'Active' : '',
  ].filter(Boolean);
  return bits.join(' · ');
}

function snapshotUser(user: AuthUser): Pick<GatewayApiKey, 'username' | 'userId' | 'email' | 'tenantId' | 'details'> {
  return {
    username: userDisplayName(user),
    userId: user.id,
    email: user.email || '',
    tenantId: user.tenantId || '',
    details: userDetails(user),
  };
}

function asUserList(value: unknown): AuthUser[] {
  let raw: unknown[] = [];
  if (Array.isArray(value)) {
    raw = value;
  } else if (value && typeof value === 'object') {
    const container = value as Record<string, unknown>;
    if (Array.isArray(container.data)) raw = container.data;
    else if (Array.isArray(container.items)) raw = container.items;
    else if (Array.isArray(container.users)) raw = container.users;
  }
  return raw
    .filter((row): row is Record<string, unknown> => Boolean(row && typeof row === 'object'))
    .map(normalizeAuthUser)
    .filter((row): row is AuthUser => Boolean(row));
}

function blankKey(): GatewayApiKey {
  const now = new Date().toISOString();
  return {
    id: createId('ak'),
    kind: 'key',
    name: '',
    description: '',
    secret: '',
    expiresAt: '',
    username: '',
    userId: '',
    email: '',
    tenantId: '',
    details: '',
    clientId: '',
    endpointId: SINGLETON_ENDPOINT_ID,
    status: 'active',
    lastUsedAt: null,
    createdAt: now,
    updatedAt: now,
    createdBy: 'Current administrator',
  };
}

function CopyField({
  label,
  value,
  isLight,
  onCopy,
  multiline,
}: {
  label: string;
  value: string;
  isLight: boolean;
  onCopy: (value: string, label: string) => void;
  multiline?: boolean;
}) {
  return (
    <div>
      <p className={labelClass(isLight)}>{label}</p>
      <div className="flex gap-2">
        {multiline ? (
          <textarea className={`${fieldClass(isLight)} font-mono`} rows={4} value={value || ''} readOnly />
        ) : (
          <input className={`${fieldClass(isLight)} font-mono`} value={value || '—'} readOnly />
        )}
        <button
          type="button"
          className={ghostBtn(isLight)}
          disabled={!value}
          onClick={() => onCopy(value, label)}
        >
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

export default function ApiKeyFormDrawer({
  open,
  initial,
  isLight,
  onClose,
  onSave,
  onToast,
}: {
  open: boolean;
  initial: GatewayApiKey | null;
  isLight: boolean;
  onClose: () => void;
  onSave: (item: GatewayApiKey) => { ok: true } | { ok: false; reason: string };
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state } = useGovernedMcp();
  const clients = state.clients;
  const [form, setForm] = useState<GatewayApiKey>(blankKey());
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [pickedUser, setPickedUser] = useState<AuthUser | null>(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [ttlSeconds, setTtlSeconds] = useState(MCP_JWT_DEFAULT_TTL_SECONDS);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(
      initial
        ? { ...initial, kind: 'key', endpointId: SINGLETON_ENDPOINT_ID, clientId: initial.clientId || '' }
        : blankKey()
    );
    setTtlSeconds(MCP_JWT_DEFAULT_TTL_SECONDS);
    setPickedUser(null);
    setError(null);
    logGovernedMcp('app', 'api_key_popup_open', { edit: Boolean(initial) });
  }, [open, initial]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setUsersLoading(true);
      setUsersError(null);
      logGovernedMcp('app', 'api_key_users_load');
      try {
        const rows = asUserList(await authservice.getUsers());
        if (cancelled) return;
        setUsers(rows);
        if (initial?.userId) {
          setPickedUser(rows.find((item) => item.id === initial.userId) || null);
        }
        logGovernedMcp('app', 'api_key_users_load_ok', { count: rows.length });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load users';
        if (!cancelled) {
          setUsers([]);
          setUsersError(message);
        }
        logGovernedMcp('error', 'api_key_users_load_failed', { message });
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const duplicateFor = (userId: string, clientId: string) => {
    if (!userId || !clientId) return undefined;
    return state.apiKeys.find(
      (row) =>
        row.id !== form.id &&
        Boolean(row.clientId) &&
        row.userId === userId &&
        row.clientId === clientId
    );
  };

  const defaultKeyName = (userName: string, client?: McpClient | null) => {
    if (!userName) return '';
    return client ? `${userName} · ${client.name}` : userName;
  };

  const issueToken = async (user: AuthUser, clientId: string, ttl = ttlSeconds) => {
    if (!clientId) {
      const message = clients.length ? 'Select a registered AI client before issuing a JWT.' : 'Register a client in Configuration → Clients before issuing a JWT.';
      setError(message);
      logGovernedMcp('warning', 'api_key_jwt_blocked', { reason: 'missing_client', userId: user.id });
      return;
    }
    const duplicate = duplicateFor(user.id, clientId);
    if (duplicate && !initial) {
      const clientName = clients.find((item) => item.id === clientId)?.name || 'this client';
      const message = `This user already has a token for ${clientName}. Open that key to reissue.`;
      setError(message);
      logGovernedMcp('warning', 'api_key_duplicate_blocked', {
        existingId: duplicate.id,
        userId: user.id,
        clientId,
      });
      return;
    }
    const roleId = asString(user.roleID);
    const tenantId = asString(user.tenantId) || TENANT_ID;
    if (!user.id || !tenantId || !user.email || !roleId) {
      const message = 'Selected user is missing id, tenant id, email, or role id.';
      setError(message);
      logGovernedMcp('warning', 'api_key_jwt_blocked', {
        reason: 'incomplete_user',
        userId: user.id,
        hasTenant: Boolean(tenantId),
        hasEmail: Boolean(user.email),
        hasRole: Boolean(roleId),
      });
      return;
    }
    setTokenLoading(true);
    setError(null);
    try {
      const issued = await governedMcpApi.issueJwt({
        userId: user.id,
        tenantId,
        email: user.email,
        name: userDisplayName(user),
        roleId,
        isAdmin: Boolean(user.isadmin),
        ttlSeconds: ttl,
      });
      setForm((prev) => ({ ...prev, secret: issued.token, expiresAt: issued.expiresAt }));
      logGovernedMcp('app', 'api_key_jwt_issued', {
        userId: user.id,
        clientId,
        expiresAt: issued.expiresAt,
        ttlSeconds: ttl,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to issue JWT';
      setError(message);
      logGovernedMcp('error', 'api_key_jwt_issue_failed', { userId: user.id, clientId, message });
    } finally {
      setTokenLoading(false);
    }
  };

  const applyUser = (userId: string) => {
    if (initial) return;
    const user = users.find((item) => item.id === userId);
    if (!user) {
      setPickedUser(null);
      setForm((prev) => ({ ...prev, userId: '', username: '', email: '', tenantId: '', details: '', secret: '', expiresAt: '' }));
      return;
    }
    setPickedUser(user);
    const snap = snapshotUser(user);
    const client = clients.find((item) => item.id === form.clientId) || null;
    const duplicate = duplicateFor(user.id, form.clientId);
    setForm((prev) => ({
      ...prev,
      ...snap,
      secret: '',
      expiresAt: '',
      name: prev.name.trim() && prev.name !== defaultKeyName(prev.username, client) ? prev.name : defaultKeyName(snap.username, client),
    }));
    logGovernedMcp('app', 'api_key_user_selected', { userId: user.id, tenantId: user.tenantId || '', clientId: form.clientId });
    if (duplicate) {
      const clientName = client?.name || 'this client';
      setError(`This user already has a token for ${clientName}. Open that key to reissue.`);
      logGovernedMcp('warning', 'api_key_duplicate_blocked', { existingId: duplicate.id, userId: user.id, clientId: form.clientId });
      return;
    }
    if (form.clientId) void issueToken(user, form.clientId);
  };

  const applyClient = (clientId: string) => {
    if (initial) return;
    const client = clients.find((item) => item.id === clientId) || null;
    const duplicate = duplicateFor(form.userId, clientId);
    setForm((prev) => ({
      ...prev,
      clientId,
      secret: '',
      expiresAt: '',
      name: prev.name.trim() && prev.name !== defaultKeyName(prev.username, clients.find((item) => item.id === prev.clientId))
        ? prev.name
        : defaultKeyName(prev.username, client),
    }));
    logGovernedMcp('app', 'api_key_client_selected', { clientId, userId: form.userId });
    if (duplicate) {
      const clientName = client?.name || 'this client';
      setError(`This user already has a token for ${clientName}. Open that key to reissue.`);
      logGovernedMcp('warning', 'api_key_duplicate_blocked', { existingId: duplicate.id, userId: form.userId, clientId });
      return;
    }
    setError(null);
    const user = pickedUser || users.find((item) => item.id === form.userId) || null;
    if (user && clientId) void issueToken(user, clientId);
  };

  const selectedUser = pickedUser || users.find((item) => item.id === form.userId) || null;
  const selectedClient = clients.find((item) => item.id === form.clientId) || null;
  const lockUser = Boolean(initial);
  const lockClient = Boolean(initial?.clientId);

  const applyClientOnEdit = (clientId: string) => {
    const client = clients.find((item) => item.id === clientId) || null;
    const duplicate = duplicateFor(form.userId, clientId);
    setForm((prev) => ({
      ...prev,
      clientId,
      name: prev.name.trim() && prev.name !== defaultKeyName(prev.username, clients.find((item) => item.id === prev.clientId))
        ? prev.name
        : defaultKeyName(prev.username, client),
    }));
    if (duplicate) {
      const clientName = client?.name || 'this client';
      setError(`This user already has a token for ${clientName}. Open that key to reissue.`);
      logGovernedMcp('warning', 'api_key_duplicate_blocked', { existingId: duplicate.id, userId: form.userId, clientId });
      return;
    }
    setError(null);
    logGovernedMcp('app', 'api_key_client_assigned', { id: form.id, clientId, userId: form.userId });
  };

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onToast('success', `${label} copied`);
      logGovernedMcp('app', 'api_key_user_field_copied', { label });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'api_key_user_field_copy_failed', { label });
    }
  };

  const submit = () => {
    if (!form.name.trim()) {
      setError('Key name is required.');
      logGovernedMcp('warning', 'api_key_validation_failed', { reason: 'missing_name' });
      return;
    }
    if (!form.userId.trim()) {
      setError('Select a user from the directory.');
      logGovernedMcp('warning', 'api_key_validation_failed', { reason: 'missing_user' });
      return;
    }
    if (!form.clientId.trim()) {
      setError(clients.length ? 'Select a registered AI client.' : 'Register a client in Configuration → Clients first.');
      logGovernedMcp('warning', 'api_key_validation_failed', { reason: 'missing_client' });
      return;
    }
    if (!form.secret.trim()) {
      setError('Issue a JWT for the selected user and client before saving.');
      logGovernedMcp('warning', 'api_key_validation_failed', { reason: 'missing_jwt' });
      return;
    }
    const result = onSave({
      ...form,
      kind: 'key',
      name: form.name.trim() || defaultKeyName(form.username, selectedClient),
      description: form.description.trim(),
      username: form.username.trim(),
      userId: form.userId.trim(),
      email: form.email.trim(),
      tenantId: form.tenantId.trim(),
      details: form.details.trim(),
      clientId: form.clientId.trim(),
      endpointId: SINGLETON_ENDPOINT_ID,
      secret: form.secret.trim(),
      expiresAt: form.expiresAt || '',
    });
    if (!result.ok) {
      setError(result.reason);
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / API Keys"
      icon={<KeyRound size={18} className="text-indigo-500" />}
      title={initial ? form.name || 'Edit API key' : 'Add API key'}
      subtitle="One JWT per user per registered client. Reissue replaces the token on this key."
      isLight={isLight}
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit} disabled={tokenLoading}>
            {initial ? 'Save' : 'Create'}
          </button>
        </div>
      }
    >
      <div className="space-y-4 max-w-2xl">
        {error ? <p className="text-sm text-red-500">{error}</p> : null}

        <div>
          <label className={labelClass(isLight)}>Client</label>
          <select
            className={fieldClass(isLight)}
            value={form.clientId}
            disabled={lockClient || tokenLoading || clients.length === 0}
            onChange={(e) => (lockUser && !lockClient ? applyClientOnEdit(e.target.value) : applyClient(e.target.value))}
          >
            <option value="">{clients.length ? 'Select a client' : 'No registered clients'}</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.name} · {clientTypeLabel(client.clientType)}
              </option>
            ))}
          </select>
          {clients.length === 0 ? (
            <p className={`text-[11px] mt-1.5 ${muted}`}>Register a client in Configuration → Clients first.</p>
          ) : null}
        </div>

        <div>
          <label className={labelClass(isLight)}>User</label>
          <select
            className={fieldClass(isLight)}
            value={form.userId}
            disabled={lockUser || usersLoading || tokenLoading}
            onChange={(e) => applyUser(e.target.value)}
          >
            <option value="">{usersLoading ? 'Loading users…' : 'Select a user'}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {userDisplayName(user)} · {user.email}
              </option>
            ))}
          </select>
          {usersError ? <p className="text-sm text-red-500 mt-1.5">{usersError}</p> : null}
          {!usersLoading && !usersError && users.length === 0 ? (
            <p className={`text-[11px] mt-1.5 ${muted}`}>No users returned from /api/auth/users.</p>
          ) : null}
        </div>

        {form.userId ? (
          <div className="grid md:grid-cols-2 gap-4">
            <CopyField label="Username" value={form.username} isLight={isLight} onCopy={copy} />
            <CopyField label="Email" value={form.email} isLight={isLight} onCopy={copy} />
            <CopyField label="User id" value={form.userId} isLight={isLight} onCopy={copy} />
            <CopyField label="Tenant id" value={form.tenantId} isLight={isLight} onCopy={copy} />
            {form.details ? (
              <div className="md:col-span-2">
                <CopyField label="Details" value={form.details} isLight={isLight} onCopy={copy} />
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className={labelClass(isLight)}>Key name</label>
            <input
              className={fieldClass(isLight)}
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Risk desk key"
            />
          </div>
          <div>
            <label className={labelClass(isLight)}>Expires</label>
            <select
              className={fieldClass(isLight)}
              value={ttlSeconds}
              onChange={(e) => {
                const next = Number(e.target.value) || MCP_JWT_DEFAULT_TTL_SECONDS;
                setTtlSeconds(next);
                logGovernedMcp('app', 'api_key_expiry_selected', { ttlSeconds: next });
                if (selectedUser) void issueToken(selectedUser, form.clientId, next);
              }}
            >
              {MCP_JWT_EXPIRY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass(isLight)}>Description</label>
          <textarea
            className={fieldClass(isLight)}
            rows={2}
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="What this JWT is allowed to call."
          />
        </div>

        <div>
          <CopyField label="JWT" value={form.secret} isLight={isLight} onCopy={copy} multiline />
          <div className="flex flex-wrap items-center gap-3 mt-2">
            <button
              type="button"
              className={ghostBtn(isLight)}
              disabled={!selectedUser || tokenLoading}
              onClick={() => {
                if (selectedUser) void issueToken(selectedUser, form.clientId);
              }}
            >
              <RefreshCw size={14} /> {tokenLoading ? 'Issuing…' : form.secret ? 'Reissue JWT' : 'Issue JWT'}
            </button>
            {!form.clientId && selectedUser ? (
              <p className={`text-[11px] ${muted}`}>Select a client to issue this JWT.</p>
            ) : null}
            {form.expiresAt ? <p className={`text-[11px] ${muted}`}>Expires {new Date(form.expiresAt).toLocaleString()}</p> : null}
          </div>
        </div>
      </div>
    </GatewayDrawer>
  );
}
